/**
 * cypherQueryService - Pure functions for building Cypher queries, parsing results,
 * filtering relationship registries, and computing graph navigation paths.
 *
 * These functions have no Jotai or React dependencies — they are purely
 * data-in / data-out, making them easy to test and reuse.
 */

import type { Relationship } from '../../../../labs/home/hooks/useRelationships';
import { getSmartLinkUrl, getDisplayName } from '../../../../labs/home/utils/smartLinks';
import { resolveNodeMetadataFromAri, type AtiNodeMetadata } from '../utils/ariUtils';
import {
	ATLASSIAN_USER_BRIDGE,
	BRIDGE_RELATIONSHIP_NAMES,
	EXTERNAL_ENTITY_BRIDGE,
	classifyTargetTypes,
	isAtiThirdParty,
	isThirdPartyUserType,
	partitionTargetTypes,
	type BridgeShape,
} from './bridgeRelationships';
import { getProductForThirdPartyId } from './productRegistry';

// ============================================================================
// Constants: content-entity-links-entity special case
// ============================================================================

/**
 * Special relationship name for content-entity-links-entity.
 * Not present in the TWG relationship registry — must be handled as a hardcoded special case.
 */
export const CONTENT_ENTITY_LINKS_ENTITY = 'content_entity_links_entity';

/**
 * Separator used to build compound relationship type keys for
 * content-entity-links-entity. Each discovered node typename gets its
 * own RelationshipInfo entry with type = `content_entity_links_entity::NodeTypename`.
 */
export const CONTENT_ENTITY_LINKS_SEPARATOR = '::';

/**
 * Checks whether a relationship type string represents a content-entity-links-entity
 * relationship (either the base constant or a per-typename compound key).
 */
export function isContentEntityLinksType(relType: string): boolean {
	return (
		relType === CONTENT_ENTITY_LINKS_ENTITY ||
		relType.startsWith(CONTENT_ENTITY_LINKS_ENTITY + CONTENT_ENTITY_LINKS_SEPARATOR)
	);
}

/**
 * Extracts the node typename from a compound content-entity-links-entity key.
 * e.g. "content_entity_links_entity::ConfluenceBlogpost" → "ConfluenceBlogpost"
 * Returns null if it's the base key or not a content-entity-links key.
 */
export function extractNodeTypeFromContentEntityKey(relType: string): string | null {
	if (!relType.startsWith(CONTENT_ENTITY_LINKS_ENTITY + CONTENT_ENTITY_LINKS_SEPARATOR)) {
		return null;
	}
	return relType.slice(CONTENT_ENTITY_LINKS_ENTITY.length + CONTENT_ENTITY_LINKS_SEPARATOR.length);
}

/**
 * Splits discovered nodes into per-typename RelationshipInfo entries
 * for content-entity-links-entity.
 *
 * Each unique typename gets its own entry with a compound key
 * `content_entity_links_entity::TypeName`, so each appears as a
 * separate row in the exploration panel.
 */
export function splitContentEntityByNodeType(nodes: DiscoveredNode[]): RelationshipInfo[] {
	const byType = new Map<string, DiscoveredNode[]>();

	for (const node of nodes) {
		let bucket = byType.get(node.typename);
		if (!bucket) {
			bucket = [];
			byType.set(node.typename, bucket);
		}
		bucket.push(node);
	}

	const results: RelationshipInfo[] = [];
	for (const [typename, typeNodes] of byType) {
		results.push({
			type: `${CONTENT_ENTITY_LINKS_ENTITY}${CONTENT_ENTITY_LINKS_SEPARATOR}${typename}`,
			count: typeNodes.length,
			nodes: typeNodes,
			sourceRelType: CONTENT_ENTITY_LINKS_ENTITY,
			targetNodeType: typename,
		});
	}

	return results;
}

/**
 * A minimal static GraphQL query for cypherQueryV2 that extracts only
 * ARI identifiers and string values — no hydration fragments.
 *
 * Used for the first "probe" call of content-entity-links-entity
 * to discover which node types are connected before hydrating.
 */
export const COUNT_ONLY_GRAPHQL_QUERY = `query TwgCountQuery($cypherQuery: String!, $params: JSON, $version: GraphStoreCypherQueryV2VersionEnum) {
  graphStore @optIn(to: ["GraphStore", "GraphStoreCypherQueryV2"]) {
    cypherQueryV2(query: $cypherQuery, params: $params, version: $version, first: 1000) {
      edges {
        node {
          __typename
          columns {
            key
            value {
              ... on GraphStoreCypherQueryV2AriNode { id }
              ... on GraphStoreCypherQueryV2NodeList { nodes { id } }
              ... on GraphStoreCypherQueryV2StringObject { str_val: value }
              ... on GraphStoreCypherQueryV2StringListObject { str_vals: values }
            }
          }
        }
      }
    }
  }
}`;

/**
 * GraphQL query that returns raw column *values* (no hydration) for a
 * cypherQueryV2 request. Used to read the stored (Cypher-queryable) metadata
 * values off a specific node by projecting the fields in the RETURN clause.
 *
 * Unlike the hydration path (which returns AGG-hydrated `data`), this reads the
 * values Cypher actually operates on — the ones stored in UGS / indexed in
 * OpenSearch. ARIs come back as `{ id }`; scalars/lists as their typed fields.
 */
export const STORED_VALUES_GRAPHQL_QUERY = `query TwgStoredValuesQuery($cypherQuery: String!, $params: JSON, $version: GraphStoreCypherQueryV2VersionEnum) {
  graphStore @optIn(to: ["GraphStore", "GraphStoreCypherQueryV2"]) {
    cypherQueryV2(query: $cypherQuery, params: $params, version: $version, first: 1) {
      edges {
        node {
          __typename
          columns {
            key
            value {
              ... on GraphStoreCypherQueryV2AriNode { id }
              ... on GraphStoreCypherQueryV2StringObject { str_val: value }
              ... on GraphStoreCypherQueryV2StringListObject { str_vals: values }
              ... on GraphStoreCypherQueryV2IntObject { int_val: value }
              ... on GraphStoreCypherQueryV2IntListObject { int_vals: values }
              ... on GraphStoreCypherQueryV2TimestampObject { timestamp: value }
              ... on GraphStoreCypherQueryV2TimestampListObject { timestamps: values }
              ... on GraphStoreCypherQueryV2BooleanObject { bool_val: value }
            }
          }
        }
      }
    }
  }
}`;

// ============================================================================
// Types
// ============================================================================

export type ExplorationDirection = 'outgoing' | 'incoming';

export type DiscoveredNode = {
	id: string;
	typename: string;
	displayName: string | null;
	url: string | null;
	properties: Record<string, any>;
	/**
	 * Internal: which Cypher branch produced this node — `direct` for a
	 * single-hop MATCH, `bridge` for a multi-hop bridge MATCH. Used by
	 * cross-batch merging in `mergeRelationshipResultsByType` to prefer
	 * direct rows over bridge rows when both surfaced the same node ARI.
	 *
	 * Stripped from the node before it reaches the display layer (and
	 * never makes it into the persistent cache except as a transient
	 * field that's overwritten on next discovery).
	 */
	_via?: 'direct' | 'bridge';
};

export type RelationshipInfo = {
	/** The relationship type name, e.g. "user_viewed_video" */
	type: string;
	/** Number of connected nodes via this relationship */
	count: number;
	/**
	 * The actual nodes discovered for this relationship type.
	 * Available from the new combined discovery query that returns nodes
	 * alongside the relationship name, avoiding a separate load query.
	 */
	nodes?: DiscoveredNode[];
	/**
	 * Present when a single Cypher relationship type has been split into
	 * per-node-type entries (e.g. content-entity-links-entity returning
	 * heterogeneous node types).  Holds the original relationship type
	 * before splitting so display layers can treat all split entries
	 * generically without hardcoding relationship names.
	 */
	sourceRelType?: string;
	/**
	 * The target node typename this entry represents when the relationship
	 * has been split by node type (e.g. "ConfluenceBlogpost", "JiraIssue").
	 */
	targetNodeType?: string;
};

/**
 * A RelationshipInfo annotated with the exploration direction it was
 * discovered from. Used internally by product-batch discovery to carry
 * direction metadata through the batching pipeline without resorting
 * to unsafe `any` casts.
 */
export type DirectedRelationshipInfo = RelationshipInfo & {
	/** The direction this relationship was discovered from. */
	_direction: ExplorationDirection;
};

/**
 * Merges multiple `RelationshipInfo` entries that share the same `type`
 * into a single entry, deduping nodes by ARI and preferring the direct
 * (single-hop) match over the bridge (multi-hop) match when both surfaced
 * the same node ARI.
 *
 * This runs AFTER all per-batch results are collected, so it correctly
 * handles the case where the standard discovery path and the
 * AtlassianUser-bridge transitive path both query the same relationship
 * label and overlap on a few real edges. The registry over-claims
 * AtlassianUser membership for most ThirdPartyUser-rooted rels, so
 * blindly skipping the transitive variant under-fetches; instead we run
 * both and dedup here.
 *
 * Split relationships (those with `targetNodeType`) are keyed by both
 * `type` AND `targetNodeType` so each split entry remains distinct.
 *
 * The returned `_via` annotation is stripped before nodes leave the
 * function so display layers see clean `DiscoveredNode` shapes.
 */
export function mergeRelationshipResultsByType(rels: RelationshipInfo[]): RelationshipInfo[] {
	type Bucket = {
		base: RelationshipInfo;
		nodesByAri: Map<string, DiscoveredNode>;
	};
	const buckets = new Map<string, Bucket>();
	const order: string[] = [];

	for (const rel of rels) {
		const key = rel.targetNodeType ? `${rel.type}::${rel.targetNodeType}` : rel.type;

		let bucket = buckets.get(key);
		if (!bucket) {
			bucket = { base: { ...rel, count: 0, nodes: [] }, nodesByAri: new Map() };
			buckets.set(key, bucket);
			order.push(key);
		}

		for (const node of rel.nodes ?? []) {
			const existing = bucket.nodesByAri.get(node.id);
			if (!existing) {
				bucket.nodesByAri.set(node.id, node);
			} else if (existing._via !== 'direct' && node._via === 'direct') {
				// Prefer the direct match — same dedup rule as
				// `parseDiscoveryResult` but applied across batches.
				bucket.nodesByAri.set(node.id, node);
			}
		}
	}

	const merged: RelationshipInfo[] = [];
	for (const key of order) {
		const bucket = buckets.get(key)!;
		// Strip the internal `_via` field so it doesn't leak into the
		// display layer or the persistent cache.
		const cleanNodes: DiscoveredNode[] = Array.from(bucket.nodesByAri.values()).map(
			({ _via, ...rest }) => rest,
		);
		merged.push({
			...bucket.base,
			count: cleanNodes.length,
			nodes: cleanNodes,
		});
	}
	return merged;
}

/**
 * Result of parsing a batch discovery response.
 */
export interface BatchDiscoveryParsedResult {
	/** Relationships that were returned (including those with count 0). */
	resolved: RelationshipInfo[];
	/**
	 * Relationship labels that were NOT included in the response because
	 * the scan limit was reached before they could be evaluated.
	 */
	truncatedLabels: string[];
}

// ============================================================================
// Bridge-aware relationship shape classification
// ============================================================================

/**
 * Per-row source label written into the synthetic `via` column when a
 * relationship's MATCH branch is built. Used by `parseDiscoveryResult` to
 * dedup the bridge multi-hop branch in favour of the direct branch when
 * both find the same target node.
 */
export const VIA_DIRECT = 'direct';
export const VIA_BRIDGE = 'bridge';

/**
 * Describes the Cypher topology a single relationship label needs at
 * discovery time, derived from the registry entry's target ATI list and
 * the supplied direction.
 *
 * - `direct` : standard `OPTIONAL MATCH (n)-[r:label]->(m)` (no bridge).
 * - `multihop-third-party` : every target is an `ati:third-party` so the
 *   third-party node is always the bridge — emit a multi-hop via
 *   `external_entity_is_third_party_entity` to project to the External
 *   graph peer.
 * - `mixed` : some targets are cloud, some are third-party — emit a
 *   discriminated MATCH per cloud typename plus one multi-hop branch for
 *   the third-party slice.
 * - `inverse-third-party-user` : the target is `ThirdPartyUser`; project
 *   the bridge node back to its `AtlassianUser` peer via
 *   `atlassian_user_links_external_user`.
 * - `transitive-from-atlassian-user` : the focused node is an
 *   `AtlassianUser`; surface a relationship rooted at the linked
 *   `ThirdPartyUser` by hopping through the AtlassianUser bridge first.
 */
/**
 * Describes how the OPPOSITE end of a transitive-from-external-peer
 * relationship should be hydrated. See `buildExternalPeerTransitiveBlock`.
 */
export type TransitiveOtherEnd = 'direct' | 'external' | 'atlassian-user';

export type RelationshipShape =
	| { kind: 'direct'; relLabel: string; direction: ExplorationDirection }
	| { kind: 'multihop-third-party'; relLabel: string; direction: ExplorationDirection }
	| {
			kind: 'mixed';
			relLabel: string;
			direction: ExplorationDirection;
			/** TWG node typenames for the cloud half — each becomes a discriminated MATCH. */
			cloudTypenames: string[];
	  }
	| { kind: 'inverse-third-party-user'; relLabel: string; direction: ExplorationDirection }
	| {
			kind: 'transitive-from-atlassian-user';
			relLabel: string;
			direction: ExplorationDirection;
	  }
	| {
			/**
			 * Focused node is an External graph node and the relationship is
			 * rooted at (or targets) a `ThirdParty<X>` typename. The Cypher
			 * starts with an External-bridge hop to project the focused node
			 * to its ThirdParty peer, then traverses the relationship from
			 * there. The opposite end may also need a bridge projection.
			 */
			kind: 'transitive-from-external-peer';
			relLabel: string;
			direction: ExplorationDirection;
			otherEnd: TransitiveOtherEnd;
	  };

/**
 * Looks up the canonical TWG typename for an ATI string, e.g.
 * `ati:cloud:bitbucket:repository` → `BitbucketRepository`.
 *
 * Returns null when no mapping is known so callers can fall back gracefully.
 */
export function atiToTypename(
	ati: string,
	atiNodeMap: Record<string, AtiNodeMetadata>,
): string | null {
	return atiNodeMap[ati]?.typename ?? null;
}

/**
 * Classifies a single registry relationship for a focused node into a
 * `RelationshipShape` describing how its discovery Cypher should be built.
 *
 * `focusedTypename` (optional) selects the AtlassianUser bridge variants
 * when the relationship would otherwise expose a `ThirdPartyUser` bridge
 * node — see the `inverse-third-party-user` and
 * `transitive-from-atlassian-user` shapes for what they emit.
 */
export function classifyRelationshipShape(
	rel: Relationship,
	relLabel: string,
	direction: ExplorationDirection,
	atiNodeMap: Record<string, AtiNodeMetadata>,
	focusedTypename?: string,
): RelationshipShape | null {
	// The "other end" target list flips with direction.
	const targets = direction === 'outgoing' ? (rel.targetType ?? []) : (rel.fromType ?? []);
	const targetTypeNames =
		direction === 'outgoing' ? (rel.targetTypeName ?? []) : (rel.fromTypeName ?? []);

	// AtlassianUser bridge — inverse case. The relationship surfaces a
	// `ThirdPartyUser` as its target, so project to the AtlassianUser peer.
	const targetsThirdPartyUser =
		targetTypeNames.includes(ATLASSIAN_USER_BRIDGE.bridgeTypename) ||
		targets.some(isThirdPartyUserType);

	if (
		targetsThirdPartyUser &&
		focusedTypename !== ATLASSIAN_USER_BRIDGE.realTypename &&
		focusedTypename !== ATLASSIAN_USER_BRIDGE.bridgeTypename
	) {
		return { kind: 'inverse-third-party-user', relLabel, direction };
	}

	const shape: BridgeShape = classifyTargetTypes(targets);

	switch (shape) {
		case 'all-cloud':
		case 'none':
			return { kind: 'direct', relLabel, direction };

		case 'all-third-party':
			return { kind: 'multihop-third-party', relLabel, direction };

		case 'mixed': {
			const { cloudAtis } = partitionTargetTypes(targets);
			const cloudTypenames = Array.from(
				new Set(
					cloudAtis.map((ati) => atiToTypename(ati, atiNodeMap)).filter((t): t is string => !!t),
				),
			);

			// Without resolved typenames we cannot build discriminated MATCH
			// blocks; fall back to a direct query so the relationship still
			// surfaces something (the bridge slice will simply be hidden by
			// downstream display filtering).
			if (cloudTypenames.length === 0) {
				return { kind: 'direct', relLabel, direction };
			}

			return { kind: 'mixed', relLabel, direction, cloudTypenames };
		}
	}
}

// ============================================================================
// Cypher query builders
// ============================================================================

/**
 * Backtick-escapes a relationship label if it contains special characters.
 */
export function escapeLabel(label: string): string {
	if (/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(label)) {
		return label;
	}
	return `\`${label}\``;
}

/**
 * Builds a single Cypher UNION ALL block for one direct (single-hop)
 * relationship. Targets `m{varSuffix}` and `r{varSuffix}` Cypher variables
 * to keep them unique across the surrounding query.
 *
 * `via` is emitted as a string literal column so the parser can dedup
 * direct vs bridge results for the same target node.
 *
 * `nodeLabel` (optional) restricts the matched target to a specific node
 * typename, e.g. `m:BitbucketRepository`, used by the `mixed` shape.
 */
function buildDirectMatchBlock(
	nodeAri: string,
	relLabel: string,
	direction: ExplorationDirection,
	varSuffix: string,
	via: typeof VIA_DIRECT | typeof VIA_BRIDGE,
	nodeLabel?: string,
): string {
	const label = escapeLabel(relLabel);
	const varName = `m${varSuffix}`;
	const relVar = `r${varSuffix}`;
	const targetExpr = nodeLabel ? `${varName}:${nodeLabel}` : varName;
	const arrowWithRel =
		direction === 'outgoing' ? `-[${relVar}:${label}]->` : `<-[${relVar}:${label}]-`;
	return [
		`MATCH (n {ari: "${nodeAri}"})`,
		`OPTIONAL MATCH (n)${arrowWithRel}(${targetExpr})`,
		`RETURN ${varName} AS c, "${relLabel}" AS relationship, "${via}" AS via`,
		`ORDER BY ${relVar}.lastUpdated DESC`,
		`LIMIT ${EXPLORATION_NODE_LIMIT}`,
	].join('\n');
}

/**
 * Builds a single Cypher UNION ALL block for a multi-hop bridge match
 * via `external_entity_is_third_party_entity`. The intermediate
 * third-party node is hidden — the returned column `c` is the External
 * graph peer.
 */
function buildExternalBridgeMatchBlock(
	nodeAri: string,
	relLabel: string,
	direction: ExplorationDirection,
	varSuffix: string,
): string {
	const label = escapeLabel(relLabel);
	const bridgeLabel = escapeLabel(EXTERNAL_ENTITY_BRIDGE.relName);
	const varName = `m${varSuffix}`;
	const relVar = `r${varSuffix}`;
	const bridgeVar = `b${varSuffix}`;
	const tpVar = `tp${varSuffix}`;
	const firstHop = direction === 'outgoing' ? `-[${relVar}:${label}]->` : `<-[${relVar}:${label}]-`;
	// External-entity bridge is always emitted "real -> third-party". The
	// node we surface is the real graph peer regardless of original direction.
	const secondHop = `<-[${bridgeVar}:${bridgeLabel}]-`;
	return [
		`MATCH (n {ari: "${nodeAri}"})`,
		`OPTIONAL MATCH (n)${firstHop}(${tpVar})${secondHop}(${varName})`,
		`RETURN ${varName} AS c, "${relLabel}" AS relationship, "${VIA_BRIDGE}" AS via`,
		`ORDER BY ${relVar}.lastUpdated DESC`,
		`LIMIT ${EXPLORATION_NODE_LIMIT}`,
	].join('\n');
}

/**
 * Builds a Cypher UNION ALL block for the AtlassianUser bridge: when the
 * focused node is an AtlassianUser, surface relationships rooted at the
 * linked ThirdPartyUser by hopping through `atlassian_user_links_external_user`.
 *
 * Direction is the original direction of `relLabel` relative to the
 * ThirdPartyUser. The query topology becomes:
 *   outgoing:  (au)-[:atlassian_user_links_external_user]->(tpu)-[:R]->(m)
 *   incoming:  (au)-[:atlassian_user_links_external_user]->(tpu)<-[:R]-(m)
 */
export function buildAtlassianUserBridgeOutwardBlock(
	atlassianUserAri: string,
	relLabel: string,
	direction: ExplorationDirection,
	varSuffix: string,
): string {
	const label = escapeLabel(relLabel);
	const auBridgeLabel = escapeLabel('atlassian_user_links_external_user');
	const varName = `m${varSuffix}`;
	const relVar = `r${varSuffix}`;
	const auBridgeVar = `aub${varSuffix}`;
	const tpuVar = `tpu${varSuffix}`;
	const secondHop =
		direction === 'outgoing' ? `-[${relVar}:${label}]->` : `<-[${relVar}:${label}]-`;
	return [
		`MATCH (n {ari: "${atlassianUserAri}"})`,
		`OPTIONAL MATCH (n)-[${auBridgeVar}:${auBridgeLabel}]->(${tpuVar})${secondHop}(${varName})`,
		`RETURN ${varName} AS c, "${relLabel}" AS relationship, "${VIA_BRIDGE}" AS via`,
		`ORDER BY ${relVar}.lastUpdated DESC`,
		`LIMIT ${EXPLORATION_NODE_LIMIT}`,
	].join('\n');
}

/**
 * Builds a Cypher UNION ALL block for the inverse AtlassianUser bridge:
 * the focused node is some non-AtlassianUser entity; we project any
 * ThirdPartyUser it would surface back to its AtlassianUser peer.
 *
 * Topology:
 *   outgoing:  (n)-[:R]->(tpu)<-[:atlassian_user_links_external_user]-(au)
 *   incoming:  (n)<-[:R]-(tpu)<-[:atlassian_user_links_external_user]-(au)
 */
export function buildAtlassianUserBridgeInwardBlock(
	nodeAri: string,
	relLabel: string,
	direction: ExplorationDirection,
	varSuffix: string,
): string {
	const label = escapeLabel(relLabel);
	const auBridgeLabel = escapeLabel('atlassian_user_links_external_user');
	const varName = `m${varSuffix}`;
	const relVar = `r${varSuffix}`;
	const auBridgeVar = `aub${varSuffix}`;
	const tpuVar = `tpu${varSuffix}`;
	const firstHop = direction === 'outgoing' ? `-[${relVar}:${label}]->` : `<-[${relVar}:${label}]-`;
	return [
		`MATCH (n {ari: "${nodeAri}"})`,
		`OPTIONAL MATCH (n)${firstHop}(${tpuVar})<-[${auBridgeVar}:${auBridgeLabel}]-(${varName})`,
		`RETURN ${varName} AS c, "${relLabel}" AS relationship, "${VIA_BRIDGE}" AS via`,
		`ORDER BY ${relVar}.lastUpdated DESC`,
		`LIMIT ${EXPLORATION_NODE_LIMIT}`,
	].join('\n');
}

/**
 * Builds a Cypher MATCH block for relationships rooted at a third-party
 * typename (e.g. `ThirdPartyDocument`, `ThirdPartyUser`) when the focused
 * node is an External graph peer. Prepends an
 * `external_entity_is_third_party_entity` hop to project the focused
 * External node to its ThirdParty peer, then runs the relationship from
 * there.
 *
 * The `otherEnd` parameter controls how the OPPOSITE end of the
 * relationship is hydrated:
 *   - `'direct'`        → return the other end as-is (typename: any)
 *   - `'external'`      → hop through `external_entity_is_third_party_entity`
 *                          to project a ThirdParty other-end to its External
 *                          peer
 *   - `'atlassian-user'` → hop through `atlassian_user_links_external_user`
 *                          to project a ThirdPartyUser other-end to its
 *                          AtlassianUser peer
 *
 * `direction` is the direction of the relationship relative to the focused
 * External node. `outgoing` means the focused node is the source of the
 * (focused-side) bridge hop; the relationship arrow then continues outward
 * from the projected ThirdParty peer.
 */
export function buildExternalPeerTransitiveBlock(
	nodeAri: string,
	relLabel: string,
	direction: ExplorationDirection,
	otherEnd: 'direct' | 'external' | 'atlassian-user',
	varSuffix: string,
): string {
	const label = escapeLabel(relLabel);
	const extBridgeLabel = escapeLabel(EXTERNAL_ENTITY_BRIDGE.relName);
	const auBridgeLabel = escapeLabel(ATLASSIAN_USER_BRIDGE.relName);

	const varName = `m${varSuffix}`;
	const relVar = `r${varSuffix}`;
	const focusBridgeVar = `feb${varSuffix}`;
	const tpFocusVar = `tpn${varSuffix}`;

	// Focused-side bridge: project focused External node forward to its
	// ThirdParty peer. The `external_entity_is_third_party_entity` edge is
	// always emitted "real → third-party" in the graph store, so an
	// External-focused node is the SOURCE of the bridge edge — we use a
	// forward arrow.
	const focusedHop = `(n)-[${focusBridgeVar}:${extBridgeLabel}]->(${tpFocusVar})`;

	// Relationship arrow from the focused ThirdParty peer
	const relArrow = direction === 'outgoing' ? `-[${relVar}:${label}]->` : `<-[${relVar}:${label}]-`;

	let otherEndPattern: string;
	if (otherEnd === 'external') {
		const otherBridgeVar = `oeb${varSuffix}`;
		const tpOtherVar = `tpo${varSuffix}`;
		otherEndPattern = `(${tpOtherVar})<-[${otherBridgeVar}:${extBridgeLabel}]-(${varName})`;
	} else if (otherEnd === 'atlassian-user') {
		const otherBridgeVar = `aub${varSuffix}`;
		const tpuOtherVar = `tpu${varSuffix}`;
		otherEndPattern = `(${tpuOtherVar})<-[${otherBridgeVar}:${auBridgeLabel}]-(${varName})`;
	} else {
		otherEndPattern = `(${varName})`;
	}

	return [
		`MATCH (n {ari: "${nodeAri}"})`,
		`OPTIONAL MATCH ${focusedHop}${relArrow}${otherEndPattern}`,
		`RETURN ${varName} AS c, "${relLabel}" AS relationship, "${VIA_BRIDGE}" AS via`,
		`ORDER BY ${relVar}.lastUpdated DESC`,
		`LIMIT ${EXPLORATION_NODE_LIMIT}`,
	].join('\n');
}

/**
 * Builds the per-relationship MATCH blocks for one `RelationshipShape`,
 * returning an array of blocks that must be joined into the surrounding
 * UNION ALL query.
 */
function buildShapeBlocks(nodeAri: string, shape: RelationshipShape, indexBase: string): string[] {
	switch (shape.kind) {
		case 'direct':
			return [
				buildDirectMatchBlock(nodeAri, shape.relLabel, shape.direction, indexBase, VIA_DIRECT),
			];

		case 'multihop-third-party':
			return [buildExternalBridgeMatchBlock(nodeAri, shape.relLabel, shape.direction, indexBase)];

		case 'mixed': {
			const blocks: string[] = [];
			shape.cloudTypenames.forEach((typename, i) => {
				blocks.push(
					buildDirectMatchBlock(
						nodeAri,
						shape.relLabel,
						shape.direction,
						`${indexBase}c${i + 1}`,
						VIA_DIRECT,
						typename,
					),
				);
			});
			blocks.push(
				buildExternalBridgeMatchBlock(nodeAri, shape.relLabel, shape.direction, `${indexBase}tp`),
			);
			return blocks;
		}

		case 'inverse-third-party-user':
			return [
				buildAtlassianUserBridgeInwardBlock(nodeAri, shape.relLabel, shape.direction, indexBase),
			];

		case 'transitive-from-atlassian-user':
			return [
				buildAtlassianUserBridgeOutwardBlock(nodeAri, shape.relLabel, shape.direction, indexBase),
			];

		case 'transitive-from-external-peer':
			return [
				buildExternalPeerTransitiveBlock(
					nodeAri,
					shape.relLabel,
					shape.direction,
					shape.otherEnd,
					indexBase,
				),
			];
	}
}

/**
 * Builds a single Cypher query using UNION ALL to discover connected nodes
 * for multiple relationship types in one request.
 *
 * Instead of counting nodes (which is expensive in the graph database),
 * returns the actual nodes alongside the relationship name. This is both
 * faster and avoids the need for a separate load query later.
 *
 * Uses OPTIONAL MATCH so that missing relationships return null instead of failing.
 * Each UNION block uses a unique variable name (m1, m2, m3, ...) as required by Cypher.
 * Results include the relationship type name as a string column so the parser
 * can group nodes by relationship type, plus a "via" column distinguishing
 * direct vs bridge matches so dedup can prefer direct rows.
 */
export function buildBatchDiscoveryQuery(
	nodeAri: string,
	relLabels: string[],
	direction: ExplorationDirection,
): string {
	const shapes: RelationshipShape[] = relLabels.map((relLabel) => ({
		kind: 'direct',
		relLabel,
		direction,
	}));
	return buildBatchDiscoveryQueryFromShapes(nodeAri, shapes);
}

/**
 * Bridge-aware variant of `buildBatchDiscoveryQuery`. Each shape is
 * expanded into one or more MATCH blocks (direct + multi-hop) according
 * to its kind, and all blocks are joined into one UNION ALL query.
 */
export function buildBatchDiscoveryQueryFromShapes(
	nodeAri: string,
	shapes: RelationshipShape[],
): string {
	const blocks: string[] = [];
	shapes.forEach((shape, index) => {
		blocks.push(...buildShapeBlocks(nodeAri, shape, `${index + 1}`));
	});
	return blocks.join('\n\nUNION ALL\n\n');
}

/** Maximum number of nodes to fetch per relationship exploration query */
export const EXPLORATION_NODE_LIMIT = 10;

/** Maximum number of nodes to display on the graph per relationship selection */
export const EXPLORATION_DISPLAY_LIMIT = 5;

/**
 * Builds a small Cypher query that resolves the External graph peer ARI
 * for a third-party bridge node. Used to rewrite the focus node when the
 * user clicks an `ari:third-party:*` entry — the discovery for the bridge
 * itself is meaningless and we want to root subsequent traversal at the
 * real (cloud:graph) peer instead.
 */
export function buildResolveExternalPeerQuery(thirdPartyAri: string): string {
	const label = escapeLabel(EXTERNAL_ENTITY_BRIDGE.relName);
	return [
		`MATCH (n {ari: "${thirdPartyAri}"})`,
		`OPTIONAL MATCH (m)-[:${label}]->(n)`,
		`RETURN m AS c`,
		`LIMIT 1`,
	].join('\n');
}

/**
 * Builds a Cypher query to load connected nodes for a specific relationship type.
 *
 * Results are limited to `EXPLORATION_NODE_LIMIT` (10) nodes to keep the
 * graph manageable for exploration. The full count is still shown in the
 * discovery panel via the batch discovery query.
 *
 * `shape` (optional) selects bridge-aware topology — when supplied, the
 * query mirrors `buildBatchDiscoveryQueryFromShapes` so toggling a single
 * relationship loads the same set of nodes the discovery panel surfaced.
 */
export function buildLoadNodesQuery(
	nodeAri: string,
	relationshipType: string,
	direction: ExplorationDirection,
	shape?: RelationshipShape,
): string {
	if (shape) {
		return buildBatchDiscoveryQueryFromShapes(nodeAri, [shape]);
	}
	const label = escapeLabel(relationshipType);
	const arrow = direction === 'outgoing' ? `-[r:${label}]->` : `<-[r:${label}]-`;

	return `MATCH (n {ari: "${nodeAri}"})${arrow}(m)\nRETURN m\nORDER BY r.lastUpdated DESC\nLIMIT ${EXPLORATION_NODE_LIMIT}`;
}

/**
 * Builds a Cypher query that reads the stored (Cypher-queryable) metadata
 * *values* for a single node, by projecting each field in the RETURN clause.
 *
 * The projected fields are the ones parsed from the node's stored-metadata jq
 * definitions (see `NodeQueryableFieldsMapAtom`). Projecting them explicitly —
 * rather than returning the whole node — yields the values stored in TWG/UGS
 * rather than the AGG-hydrated values.
 *
 * A `nodeLabel` (the TWG typename, e.g. `ConfluencePage`) MUST be supplied for
 * the projection to resolve: AGG cannot infer a node's type from its ARI alone,
 * so `MATCH (n {ari})` + `RETURN n.field` fails with
 * "Field 'x' cannot be resolved on an unknown node type … please provide a node
 * label." Adding the label (`MATCH (n:ConfluencePage {ari})`) fixes it.
 *
 * Returns an empty string when there are no fields to project.
 */
export function buildNodeStoredValuesQuery(
	nodeAri: string,
	fields: string[],
	nodeLabel?: string,
): string {
	if (fields.length === 0) {
		return '';
	}
	const labelPart = nodeLabel ? `:${escapeLabel(nodeLabel)}` : '';
	const projections = fields.map((f) => `n.${escapeLabel(f)} AS ${escapeLabel(f)}`).join(', ');
	return `MATCH (n${labelPart} {ari: "${nodeAri}"})\nRETURN ${projections}\nLIMIT 1`;
}

// ============================================================================
// Result parsing
// ============================================================================

/**
 * Extracts the relationship type name from a column value.
 */
function extractRelationshipName(val: any): string | null {
	if (typeof val === 'string') {
		return val;
	}
	if (val && typeof val === 'object') {
		return val.str_val ?? val.value ?? null;
	}
	return null;
}

/**
 * Hydrates a raw TWG entity value (the `{ id, data }` wrapper from an AGG
 * Cypher response column) into a resolved node with clean properties.
 *
 * This is the single place where:
 * - Typename is resolved from ARI → ATI metadata → `__typename` fallback
 * - Property keys are normalised (TypeName_ prefix stripped)
 * - URL and display name are extracted
 * - Nested graph-node objects are filtered from properties
 *
 * @param value  The raw column value (`col.value`) containing `id` and `data`.
 * @param columnKey  The column key (e.g. `"c"`, `"m"`) used for display name resolution.
 * @param atiNodeMap  Optional ATI → metadata map for typename resolution.
 */
function hydrateEntityValue(
	value: any,
	columnKey: string,
	atiNodeMap?: Record<string, AtiNodeMetadata>,
): DiscoveredNode | null {
	if (!value) {
		return null;
	}

	const id = value.id || value.ari || value.nodeId;
	if (!id) {
		return null;
	}

	const data = value.data || {};
	let typename = '';

	// Primary: resolve from ARI via ATI metadata
	if (atiNodeMap && typeof id === 'string' && id.startsWith('ari:')) {
		const metadata = resolveNodeMetadataFromAri(id, atiNodeMap);
		if (metadata) {
			typename = metadata.typename;
		}
	}

	// Fallback: use GraphQL __typename
	if (!typename) {
		typename = data.__typename || 'Unknown';
	}

	// Use the GraphQL __typename for property normalisation — the response
	// keys are prefixed with the hydration type (e.g. "AtlassianAccountUser_name"),
	// not the TWG node name ("IdentityUser").
	const normalisationTypename = data.__typename || typename;

	// Normalize properties: strip TypeName_ prefix from keys
	const normalized: Record<string, any> = {};
	for (const [rawKey, val] of Object.entries(data)) {
		if (rawKey.startsWith('__') || rawKey === 'id' || rawKey === 'ari') {
			continue;
		}
		const cleanKey = stripTypenamePrefix(rawKey, normalisationTypename);
		normalized[cleanKey] = val;
	}

	const cleanEntity = { ...normalized, __typename: normalisationTypename, id };
	const url = getSmartLinkUrl(cleanEntity) ?? null;
	let displayName = getDisplayName(columnKey, cleanEntity) ?? null;

	// Build properties excluding nested graph nodes
	const properties: Record<string, any> = {};
	for (const [key, v] of Object.entries(normalized)) {
		if (
			v &&
			typeof v === 'object' &&
			!Array.isArray(v) &&
			(v.__typename || v.typename) &&
			(v.id || v.ari || v.nodeId)
		) {
			continue;
		}
		properties[key] = v;
	}

	// External-graph nodes (`ari:cloud:graph::*`) carry a `thirdPartyId`
	// pointing at the originating connector. Resolve that to the connector's
	// human display name and stash it on `properties.productDisplayName`,
	// so display layers can replace "External" with the real product name
	// (e.g. "External Repository" → "GitHub Repository") without mutating
	// the underlying typename — caches and hydration paths stay stable.
	const thirdPartyId = typeof properties.thirdPartyId === 'string' ? properties.thirdPartyId : null;
	if (thirdPartyId) {
		const product = getProductForThirdPartyId(thirdPartyId);
		if (product) {
			properties.productDisplayName = product.displayName;
			if (typeof displayName === 'string' && displayName.startsWith('External ')) {
				displayName = `${product.displayName} ${displayName.slice('External '.length)}`;
			}
		}
	}

	return { id, typename, displayName, url, properties };
}

/**
 * Extracts a single node's data from a column value (the "c" column).
 * Returns null if the value is empty (OPTIONAL MATCH found no node).
 */
function extractNodeFromColumn(
	col: any,
	atiNodeMap?: Record<string, AtiNodeMetadata>,
): DiscoveredNode | null {
	return hydrateEntityValue(col?.value, 'c', atiNodeMap);
}

/**
 * Parses a batch discovery result that returns actual nodes and relationship names.
 *
 * The response contains one row per discovered node (or a null-node row for
 * relationship types with no connections). Each row has columns:
 * - `c`: the connected node (or null if OPTIONAL MATCH found nothing)
 * - `relationship`: the relationship type name as a string
 * - `via` (optional): "direct" or "bridge" indicating whether the row came
 *   from a direct MATCH or a bridge multi-hop MATCH. Used to dedup the
 *   same target ARI in favour of the direct row.
 *
 * Groups rows by relationship type, extracts node data, and computes counts.
 * Also detects scan-limit truncation: if a queried relationship type doesn't
 * appear at all in the response, it's reported as truncated.
 *
 * Note: when an `ati:third-party` node ever leaks through (e.g. a
 * misclassified relationship), it is dropped from the resulting node list
 * so the bridge abstraction is preserved end-to-end.
 */
export function parseDiscoveryResult(
	result: any,
	relLabels: string[],
	atiNodeMap?: Record<string, AtiNodeMetadata>,
): BatchDiscoveryParsedResult {
	const edges = result?.data?.graphStore?.cypherQueryV2?.edges;
	if (!Array.isArray(edges)) {
		return { resolved: [], truncatedLabels: [...relLabels] };
	}

	// Build a reverse lookup from typename → ATI for fast bridge-node
	// detection. This is built once per call (not per row) so the O(N)
	// map iteration only happens once.
	const typenameToAti: Record<string, string> = {};
	if (atiNodeMap) {
		for (const [ati, meta] of Object.entries(atiNodeMap)) {
			typenameToAti[meta.typename] = ati;
		}
	}

	// Group nodes by relationship type. We track each ARI's `via` so we can
	// keep the direct row when the same node appears via a bridge multi-hop
	// as well.
	type NodeEntry = { node: DiscoveredNode; via: string };
	const entriesByRelType = new Map<string, Map<string, NodeEntry>>();
	const seenRelTypes = new Set<string>();

	for (const edge of edges) {
		const columns = edge?.node?.columns;
		if (!Array.isArray(columns)) {
			continue;
		}

		let relName: string | null = null;
		let node: DiscoveredNode | null = null;
		let via: string = VIA_DIRECT;

		for (const col of columns) {
			if (col.key === 'relationship') {
				relName = extractRelationshipName(col.value);
			} else if (col.key === 'c') {
				node = extractNodeFromColumn(col, atiNodeMap);
			} else if (col.key === 'via') {
				via = extractRelationshipName(col.value) ?? VIA_DIRECT;
			}
		}

		if (!relName) {
			continue;
		}

		seenRelTypes.add(relName);

		if (!node) {
			continue;
		}

		// Defensive: never let a third-party bridge node leak into results.
		// `node.typename` is a PascalCase string (e.g. "ThirdPartyRemoteLink"),
		// NOT an ATI. Resolve it to its ATI via the reverse lookup built
		// above, then run through `isAtiThirdParty`. Falls back to false
		// (don't drop) when the typename isn't in the schema map.
		if (isAtiThirdParty(typenameToAti[node.typename] ?? null)) {
			continue;
		}

		let entries = entriesByRelType.get(relName);
		if (!entries) {
			entries = new Map<string, NodeEntry>();
			entriesByRelType.set(relName, entries);
		}

		// Annotate the node with its `_via` so cross-batch merging
		// (`mergeRelationshipResultsByType`) can apply the same
		// direct-over-bridge preference once results are combined.
		const annotatedNode: DiscoveredNode = {
			...node,
			_via: via === VIA_BRIDGE ? 'bridge' : 'direct',
		};

		const existing = entries.get(node.id);
		if (!existing) {
			entries.set(node.id, { node: annotatedNode, via });
		} else if (existing.via !== VIA_DIRECT && via === VIA_DIRECT) {
			// Prefer the direct match over the bridge match so the panel and
			// edge layer never show a bridge connection alongside a direct one.
			entries.set(node.id, { node: annotatedNode, via });
		}
	}

	// Convert per-rel maps back into ordered DiscoveredNode arrays.
	const nodesByRelType = new Map<string, DiscoveredNode[]>();
	for (const [relName, entries] of entriesByRelType) {
		nodesByRelType.set(
			relName,
			Array.from(entries.values()).map((e) => e.node),
		);
	}

	// Build resolved list preserving input order, only for types that appeared in the response.
	//
	// Truncation detection: a label missing from the response can mean either:
	// (a) Scan-limit truncation — the server stopped processing before reaching
	//     this label. In this case ALL labels after the last seen label are missing.
	// (b) Upstream planner bug — the query planner generates a broken execution
	//     plan for certain polymorphic target types, causing their OPTIONAL MATCH
	//     to return zero rows instead of a null row. The label is absent from the
	//     response, but labels AFTER it may still have results.
	//
	// To distinguish the two: we only treat labels as "truncated" if they form
	// a contiguous suffix of missing labels (i.e., everything after the last
	// label that returned results). Labels that are missing but followed by
	// labels that DO have results are treated as zero-count (planner bug),
	// not as truncated.
	const resolved: RelationshipInfo[] = [];
	const truncatedLabels: string[] = [];

	// Find the index of the last label that appeared in the response
	let lastSeenIndex = -1;
	for (let i = relLabels.length - 1; i >= 0; i--) {
		if (seenRelTypes.has(relLabels[i])) {
			lastSeenIndex = i;
			break;
		}
	}

	for (let i = 0; i < relLabels.length; i++) {
		const label = relLabels[i];
		if (seenRelTypes.has(label)) {
			const nodes = nodesByRelType.get(label) || [];
			resolved.push({ type: label, count: nodes.length, nodes });
		} else if (i > lastSeenIndex && lastSeenIndex >= 0) {
			// This label is in the contiguous suffix after the last seen label
			// — likely truncated by scan limit
			truncatedLabels.push(label);
		} else {
			// This label is missing but there are later labels with results
			// — upstream planner bug, treat as zero-count
			resolved.push({ type: label, count: 0, nodes: [] });
		}
	}

	return { resolved, truncatedLabels };
}

/**
 * Extracts child node ARIs from a raw network result.
 * Used to populate the parent map for breadcrumb path computation.
 */
export function extractChildArisFromResult(result: any): string[] {
	const edges = result?.data?.graphStore?.cypherQueryV2?.edges;
	if (!Array.isArray(edges)) {
		return [];
	}

	const aris: string[] = [];
	const seen = new Set<string>();

	for (const edge of edges) {
		const columns = edge?.node?.columns;
		if (!Array.isArray(columns)) {
			continue;
		}
		for (const col of columns) {
			const value = col?.value;
			if (!value) {
				continue;
			}
			const id = value.id || value.ari || value.nodeId;
			if (id && !seen.has(id)) {
				seen.add(id);
				aris.push(id);
			}
		}
	}

	return aris;
}

/**
 * Strips the `TypeName_` prefix from a property key if it matches the
 * entity's `__typename`.
 * e.g. "AtlassianAccountUser_name" → "name" when typename is "AtlassianAccountUser".
 * Keys that don't have the prefix are returned as-is.
 */
function stripTypenamePrefix(propKey: string, typename: string): string {
	if (typename && propKey.startsWith(`${typename}_`)) {
		return propKey.substring(typename.length + 1);
	}
	return propKey;
}

/**
 * Extracts ReactFlow-compatible node data from a raw network result.
 *
 * Normalizes property keys by stripping `TypeName_` prefixes (e.g.,
 * `AtlassianAccountUser_name` → `name`) so that downstream display name
 * and URL extraction works consistently with the Results-mode path in
 * `useObjectsGraph.ts`.
 */
export function extractNodesFromResult(
	result: any,
	atiNodeMap?: Record<string, AtiNodeMetadata>,
): Array<{
	id: string;
	typename: string;
	displayName: string | null;
	url: string | null;
	properties: Record<string, any>;
}> {
	const edges = result?.data?.graphStore?.cypherQueryV2?.edges;
	if (!Array.isArray(edges)) {
		return [];
	}

	const nodes: Array<{
		id: string;
		typename: string;
		displayName: string | null;
		url: string | null;
		properties: Record<string, any>;
	}> = [];
	const seenIds = new Set<string>();

	for (const edge of edges) {
		const columns = edge?.node?.columns;
		if (!Array.isArray(columns)) {
			continue;
		}

		for (const col of columns) {
			const value = col?.value;
			if (!value) {
				continue;
			}

			// Pre-check for id to avoid hydrating duplicates
			const id = value.id || value.ari || value.nodeId;
			if (!id || seenIds.has(id)) {
				continue;
			}

			const node = hydrateEntityValue(value, col.key, atiNodeMap);
			if (node) {
				seenIds.add(node.id);
				nodes.push(node);
			}
		}
	}

	return nodes;
}

/**
 * Extracts a plain JS value from a single cypherQueryV2 column value (one of
 * the `GraphStoreCypherQueryV2*` union members returned by
 * `STORED_VALUES_GRAPHQL_QUERY`).
 *
 * - ARI node        → the ARI string (`id`)
 * - node list       → array of ARI strings
 * - string/int/bool → the primitive value
 * - list variants   → the array of values
 * - timestamp(s)    → epoch millis (number) / array of numbers
 *
 * Returns `undefined` for empty / unrecognised values so callers can treat the
 * field as "not stored / null".
 */
export function extractColumnScalarValue(value: any): unknown {
	if (value === null || value === undefined || typeof value !== 'object') {
		return undefined;
	}
	if (typeof value.id === 'string') {
		return value.id;
	}
	if (Array.isArray(value.nodes)) {
		return value.nodes.map((n: any) => n?.id).filter((id: unknown): id is string => !!id);
	}
	if (typeof value.str_val === 'string') {
		return value.str_val;
	}
	if (Array.isArray(value.str_vals)) {
		return value.str_vals;
	}
	if (typeof value.int_val === 'number') {
		return value.int_val;
	}
	if (Array.isArray(value.int_vals)) {
		return value.int_vals;
	}
	if (typeof value.timestamp === 'number') {
		return value.timestamp;
	}
	if (Array.isArray(value.timestamps)) {
		return value.timestamps;
	}
	if (typeof value.bool_val === 'boolean') {
		return value.bool_val;
	}
	return undefined;
}

/**
 * Parses a `STORED_VALUES_GRAPHQL_QUERY` response (a single row projecting the
 * node's stored fields) into a `{ field: value }` map. Columns with empty /
 * unrecognised values are omitted.
 */
export function parseStoredValuesResult(result: any): Record<string, unknown> {
	const edges = result?.data?.graphStore?.cypherQueryV2?.edges;
	if (!Array.isArray(edges) || edges.length === 0) {
		return {};
	}

	const columns = edges[0]?.node?.columns;
	if (!Array.isArray(columns)) {
		return {};
	}

	const out: Record<string, unknown> = {};
	for (const col of columns) {
		if (!col?.key) {
			continue;
		}
		const parsed = extractColumnScalarValue(col.value);
		if (parsed !== undefined && parsed !== null) {
			out[col.key] = parsed;
		}
	}
	return out;
}

// ============================================================================
// Relationship registry helpers
// ============================================================================

/**
 * Checks if a relationship is available in production.
 */
function isAvailableInProd(rel: Relationship): boolean {
	if (!rel.availableIn || rel.availableIn.length === 0) {
		return true;
	}
	return rel.availableIn.some(
		(env) => env.toLowerCase() === 'prod' || env.toLowerCase() === 'production',
	);
}

/**
 * Returns the target node type labels for a given relationship type and direction.
 *
 * When exploring outgoing relationships, the target is the relationship's targetType/targetTypeName.
 * When exploring incoming relationships, the target is the relationship's fromType/fromTypeName.
 *
 * The relationship type is expected in Cypher underscore format.
 */
export function getTargetNodeLabels(
	allRelationships: Record<string, Relationship>,
	relationshipType: string,
	direction: ExplorationDirection,
): string[] {
	for (const [id, rel] of Object.entries(allRelationships)) {
		const cypherRelName = (rel.name || id).replace(/-/g, '_');
		if (cypherRelName !== relationshipType) {
			continue;
		}

		if (direction === 'outgoing') {
			return [...(rel.targetType || []), ...(rel.targetTypeName || [])].filter((t) => t !== '*');
		} else {
			return [...(rel.fromType || []), ...(rel.fromTypeName || [])].filter((t) => t !== '*');
		}
	}

	return [];
}

/**
 * Matches a relationship endpoint against a node using either schema naming
 * system. The registry can describe a node by canonical typename or ATI,
 * while a hydrated graph node may use the other representation.
 */
export function relationshipEndpointMatchesNode(
	endpointTypes: string[],
	endpointTypeNames: string[],
	nodeTypename: string,
	nodeAtiTypes: string[] = [],
): boolean {
	const normalizedNodeTypename = nodeTypename.toLowerCase();
	const normalizedNodeAtiTypes = new Set(nodeAtiTypes.map((type) => type.toLowerCase()));

	return (
		endpointTypeNames.some(
			(type) => type === '*' || type.toLowerCase() === normalizedNodeTypename,
		) ||
		endpointTypes.some(
			(type) =>
				type === '*' ||
				type.toLowerCase() === normalizedNodeTypename ||
				normalizedNodeAtiTypes.has(type.toLowerCase()),
		)
	);
}

/**
 * Filters relationship types that are relevant to a given node typename
 * based on the relationship registry.
 *
 * Filters out:
 * - Hidden relationships
 * - Deleted relationships
 * - Relationships not available in prod
 * - Inferred relationships (category: "inferred")
 * - Bridge relationships (suppressed; surfaced via multi-hop projections)
 *
 * Converts hyphen-delimited names to underscore-delimited for Cypher.
 */
export function getRelevantRelationshipTypes(
	allRelationships: Record<string, Relationship>,
	nodeTypename: string,
	direction: ExplorationDirection,
	nodeAtiTypes: string[] = [],
): string[] {
	const relevantTypes: string[] = [];

	for (const [id, rel] of Object.entries(allRelationships)) {
		if (rel.hidden || rel.deleted || !isAvailableInProd(rel)) {
			continue;
		}

		if (rel.category?.toLowerCase() === 'inferred') {
			continue;
		}

		const cypherRelName = (rel.name || id).replace(/-/g, '_');

		if (
			cypherRelName === 'content_referenced_entity' ||
			cypherRelName === 'comment_content_entity'
		) {
			continue;
		}

		// Bridge relationships are an internal implementation detail — the
		// graph explorer surfaces their *projections* via multi-hop queries
		// rather than the bridge itself. Suppress them everywhere.
		if (BRIDGE_RELATIONSHIP_NAMES.includes(cypherRelName)) {
			continue;
		}

		if (direction === 'outgoing') {
			const matches = relationshipEndpointMatchesNode(
				rel.fromType || [],
				rel.fromTypeName || [],
				nodeTypename,
				nodeAtiTypes,
			);
			if (matches) {
				relevantTypes.push(cypherRelName);
			}
		} else {
			const matches = relationshipEndpointMatchesNode(
				rel.targetType || [],
				rel.targetTypeName || [],
				nodeTypename,
				nodeAtiTypes,
			);
			if (matches) {
				relevantTypes.push(cypherRelName);
			}
		}
	}

	return [...new Set(relevantTypes)];
}

/**
 * Enumerates synthetic `ProductBatchEntry` items for the AtlassianUser
 * bridge: every relationship rooted at `ThirdPartyUser` is surfaced from
 * the focused AtlassianUser via a multi-hop through
 * `atlassian_user_links_external_user`.
 *
 * The returned entries carry `shape: 'transitive-from-atlassian-user'`
 * so they bypass classification in the discovery pipeline.
 *
 * Bridge / inferred / hidden / deleted relationships are skipped, as is
 * the AtlassianUser bridge itself.
 */
export function enumerateAtlassianUserTransitiveEntries(
	allRelationships: Record<string, Relationship>,
): ProductBatchEntry[] {
	const entries: ProductBatchEntry[] = [];
	const tpuTypename = ATLASSIAN_USER_BRIDGE.bridgeTypename.toLowerCase();

	// Note: relationships whose registry entry lists *both* AtlassianUser
	// and ThirdPartyUser as fromTypeName are still emitted as transitive
	// entries. The registry over-claims AtlassianUser membership for
	// most ThirdPartyUser-rooted rels, but in practice only a small number
	// of edges actually exist on the AtlassianUser side. Running both
	// queries lets us see whichever one(s) actually have edges, and
	// `mergeRelationshipResults` (in `explorationActions.ts`) dedups any
	// node that comes back via both paths in favour of the direct match.
	for (const [id, rel] of Object.entries(allRelationships)) {
		if (rel.hidden || rel.deleted || !isAvailableInProd(rel)) continue;
		if (rel.category?.toLowerCase() === 'inferred') continue;

		const cypherRelName = (rel.name || id).replace(/-/g, '_');
		if (BRIDGE_RELATIONSHIP_NAMES.includes(cypherRelName)) continue;
		if (
			cypherRelName === 'content_referenced_entity' ||
			cypherRelName === 'comment_content_entity'
		) {
			continue;
		}

		const fromTypeNames = (rel.fromTypeName ?? []).map((t) => t.toLowerCase());
		const targetTypeNames = (rel.targetTypeName ?? []).map((t) => t.toLowerCase());

		if (fromTypeNames.includes(tpuTypename)) {
			entries.push({
				relLabel: cypherRelName,
				direction: 'outgoing',
				relationship: rel,
				shape: {
					kind: 'transitive-from-atlassian-user',
					relLabel: cypherRelName,
					direction: 'outgoing',
				},
			});
		}
		if (targetTypeNames.includes(tpuTypename)) {
			entries.push({
				relLabel: cypherRelName,
				direction: 'incoming',
				relationship: rel,
				shape: {
					kind: 'transitive-from-atlassian-user',
					relLabel: cypherRelName,
					direction: 'incoming',
				},
			});
		}
	}

	return entries;
}

/**
 * Enumerates synthetic `ProductBatchEntry` items for the External-peer
 * bridge: when the focused node is an `External<X>` typename, every
 * relationship rooted at (or targeting) the corresponding `ThirdParty<X>`
 * typename is surfaced via a multi-hop through the External bridge.
 *
 * The opposite end of each rel is classified to determine whether IT also
 * needs a bridge projection:
 *   - all targets cloud / non-third-party       → otherEnd = 'direct'
 *   - all targets third-party (incl. ThirdPartyUser) → bridge accordingly
 *   - mixed                                      → emit one entry per
 *                                                  branch (advanced)
 *
 * Bridge / inferred / hidden / deleted relationships are skipped.
 *
 * @param allRelationships full registry
 * @param focusedTypename  the focused node's TWG typename (e.g.
 *                         "ExternalDocument"). Must start with "External"
 *                         for this enumeration to do anything.
 */
export function enumerateExternalPeerTransitiveEntries(
	allRelationships: Record<string, Relationship>,
	focusedTypename: string,
): ProductBatchEntry[] {
	if (!focusedTypename.startsWith('External')) return [];

	// Map "ExternalDocument" → "ThirdPartyDocument" (we project across the
	// External bridge, so the equivalent ThirdParty typename matches the
	// "External" suffix swapped with "ThirdParty").
	const thirdPartyEquivalent = `ThirdParty${focusedTypename.slice('External'.length)}`;
	const tpTypenameLower = thirdPartyEquivalent.toLowerCase();
	const tpUserTypenameLower = ATLASSIAN_USER_BRIDGE.bridgeTypename.toLowerCase();

	const entries: ProductBatchEntry[] = [];

	for (const [id, rel] of Object.entries(allRelationships)) {
		if (rel.hidden || rel.deleted || !isAvailableInProd(rel)) continue;
		if (rel.category?.toLowerCase() === 'inferred') continue;

		const cypherRelName = (rel.name || id).replace(/-/g, '_');
		if (BRIDGE_RELATIONSHIP_NAMES.includes(cypherRelName)) continue;
		if (
			cypherRelName === 'content_referenced_entity' ||
			cypherRelName === 'comment_content_entity'
		) {
			continue;
		}

		const fromNamesLower = (rel.fromTypeName ?? []).map((t) => t.toLowerCase());
		const targetNamesLower = (rel.targetTypeName ?? []).map((t) => t.toLowerCase());

		// Decide whether the relationship's source side claims the focused
		// node's third-party equivalent; if so, the relationship is OUTGOING
		// from the focused (External) node's perspective.
		const sourceMatch = fromNamesLower.includes(tpTypenameLower);
		// Same for the target side → INCOMING.
		const targetMatch = targetNamesLower.includes(tpTypenameLower);

		if (sourceMatch) {
			const otherEnd = pickTransitiveOtherEnd(rel, /* otherSide = */ 'target', tpUserTypenameLower);
			entries.push({
				relLabel: cypherRelName,
				direction: 'outgoing',
				relationship: rel,
				shape: {
					kind: 'transitive-from-external-peer',
					relLabel: cypherRelName,
					direction: 'outgoing',
					otherEnd,
				},
			});
		}

		if (targetMatch) {
			const otherEnd = pickTransitiveOtherEnd(rel, /* otherSide = */ 'from', tpUserTypenameLower);
			entries.push({
				relLabel: cypherRelName,
				direction: 'incoming',
				relationship: rel,
				shape: {
					kind: 'transitive-from-external-peer',
					relLabel: cypherRelName,
					direction: 'incoming',
					otherEnd,
				},
			});
		}
	}

	return entries;
}

/**
 * Determines how the OPPOSITE end of a transitive-from-external-peer
 * relationship should be hydrated, given which side of the rel is the
 * "other" side.
 *
 * The decision rules (in order):
 *   1. If the other side includes ThirdPartyUser → bridge as 'atlassian-user'
 *      (project to the AtlassianUser peer for display).
 *   2. If the other side is dominantly third-party (any name starts with
 *      "ThirdParty") → bridge as 'external' (project to the External peer).
 *   3. Otherwise → 'direct' (no projection needed).
 *
 * Mixed cases default to 'external' because the registry typically
 * over-represents third-party variants of the same logical entity.
 */
function pickTransitiveOtherEnd(
	rel: Relationship,
	otherSide: 'from' | 'target',
	tpUserTypenameLower: string,
): TransitiveOtherEnd {
	const names = otherSide === 'from' ? (rel.fromTypeName ?? []) : (rel.targetTypeName ?? []);
	const lower = names.map((t) => t.toLowerCase());

	if (lower.includes(tpUserTypenameLower)) return 'atlassian-user';
	if (lower.some((n) => n.startsWith('thirdparty'))) return 'external';
	return 'direct';
}

// ============================================================================
// Product-based relationship grouping
// ============================================================================

/**
 * Extracts the product segment from an ATI/ARI type identifier.
 *
 * ATI format: `ati:cloud:<product>:<entityType>`
 * Example: `ati:cloud:confluence:space` → `"confluence"`
 *          `ati:cloud:jira:devops-component` → `"jira"`
 *          `ati:cloud:graph:message` → `"graph"`
 *
 * Returns `"unknown"` if the format doesn't match.
 */
export function extractProductFromAti(atiType: string): string {
	const parts = atiType.split(':');
	return parts.length >= 4 ? parts[2] : 'unknown';
}

/**
 * Entry in a product-based relationship batch.
 */
export interface ProductBatchEntry {
	/** The Cypher relationship label (underscore-delimited). */
	relLabel: string;
	/** Whether this relationship is outgoing or incoming for the focused node. */
	direction: ExplorationDirection;
	/**
	 * The originating registry entry, threaded through so the discovery
	 * pipeline can classify per-relationship Cypher topology (direct vs
	 * multi-hop bridge vs mixed) without doing a second registry lookup.
	 */
	relationship?: Relationship;
	/**
	 * Optional precomputed Cypher shape. When set, the discovery pipeline
	 * uses this verbatim and skips re-running the classifier. Useful for
	 * synthetic entries (e.g. AtlassianUser → ThirdPartyUser transitive
	 * rels) that don't fit the standard "node typename matches fromType"
	 * registry lookup.
	 */
	shape?: RelationshipShape;
}

/**
 * Minimum number of relationships a product group must have to remain a
 * standalone batch. Smaller groups are merged into the catch-all `"other"` bucket.
 */
export const MIN_PRODUCT_GROUP_SIZE = 10;

/**
 * Maximum number of parallel batches. If the product grouping produces more
 * groups than this, the smallest are folded into `"other"` until the cap is met.
 */
export const MAX_PRODUCT_BATCHES = 6;

/**
 * Groups relevant relationships for a node by the product of the "other end"
 * ATI types. This enables parallel discovery queries where each batch covers
 * a product domain (or a combined domain for cross-product relationships).
 *
 * The grouping key is the sorted, comma-joined set of products from the
 * other end's ATI types. For example:
 * - An outgoing relationship with `targetType: ["ati:cloud:confluence:page"]`
 *   goes into the `"confluence"` group.
 * - An outgoing relationship with `targetType: ["ati:cloud:jira:work-item", "ati:cloud:graph:work-item"]`
 *   goes into the `"graph,jira"` group.
 *
 * To keep the number of parallel HTTP calls reasonable, groups with fewer
 * than `MIN_PRODUCT_GROUP_SIZE` relationships are merged into a catch-all
 * `"other"` bucket, and the total number of groups is capped at
 * `MAX_PRODUCT_BATCHES` (smallest groups are folded into `"other"` first).
 *
 * Within each product group, relationships of both directions may coexist —
 * which is fine because the Cypher query builder handles direction per-label.
 *
 * @returns A Map from product key to arrays of batch entries, each annotated
 * with direction. The same relationship can appear in at most one group.
 */
export function groupRelationshipsByProduct(
	allRelationships: Record<string, Relationship>,
	nodeTypename: string,
	nodeAtiTypes: string[] = [],
): Map<string, ProductBatchEntry[]> {
	const groups = new Map<string, ProductBatchEntry[]>();

	for (const [id, rel] of Object.entries(allRelationships)) {
		if (rel.hidden || rel.deleted || !isAvailableInProd(rel)) {
			continue;
		}
		if (rel.category?.toLowerCase() === 'inferred') {
			continue;
		}

		const cypherRelName = (rel.name || id).replace(/-/g, '_');

		if (
			cypherRelName === 'content_referenced_entity' ||
			cypherRelName === 'comment_content_entity'
		) {
			continue;
		}

		// Bridge relationships are surfaced only via multi-hop projections
		// of other relationships, never directly.
		if (BRIDGE_RELATIONSHIP_NAMES.includes(cypherRelName)) {
			continue;
		}

		// Check outgoing: node matches fromType/fromTypeName
		const isOutgoing = relationshipEndpointMatchesNode(
			rel.fromType || [],
			rel.fromTypeName || [],
			nodeTypename,
			nodeAtiTypes,
		);

		// Check incoming: node matches targetType/targetTypeName
		const isIncoming = relationshipEndpointMatchesNode(
			rel.targetType || [],
			rel.targetTypeName || [],
			nodeTypename,
			nodeAtiTypes,
		);

		if (isOutgoing) {
			// "Other end" for outgoing = targetType ATI types
			const otherEndProducts = (rel.targetType || [])
				.map(extractProductFromAti)
				.filter((p) => p !== 'unknown');
			const productKey =
				otherEndProducts.length > 0 ? [...new Set(otherEndProducts)].sort().join(',') : 'other';
			let group = groups.get(productKey);
			if (!group) {
				group = [];
				groups.set(productKey, group);
			}
			group.push({ relLabel: cypherRelName, direction: 'outgoing', relationship: rel });
		}

		if (isIncoming) {
			// "Other end" for incoming = fromType ATI types
			const otherEndProducts = (rel.fromType || [])
				.map(extractProductFromAti)
				.filter((p) => p !== 'unknown');
			const productKey =
				otherEndProducts.length > 0 ? [...new Set(otherEndProducts)].sort().join(',') : 'other';
			let group = groups.get(productKey);
			if (!group) {
				group = [];
				groups.set(productKey, group);
			}
			group.push({ relLabel: cypherRelName, direction: 'incoming', relationship: rel });
		}
	}

	// Deduplicate within each group (same relLabel+direction)
	for (const [key, entries] of groups) {
		const seen = new Set<string>();
		const deduped = entries.filter((e) => {
			const entryKey = `${e.relLabel}:${e.direction}`;
			if (seen.has(entryKey)) {
				return false;
			}
			seen.add(entryKey);
			return true;
		});
		groups.set(key, deduped);
	}

	// Merge small groups into the catch-all "other" bucket.
	// A group is considered "small" when NEITHER direction on its own
	// reaches MIN_PRODUCT_GROUP_SIZE. When a group is large enough in
	// one direction but tiny in the other, the tiny-direction entries
	// are moved to "other" (since DiscoverProductBatchAtom issues a
	// separate HTTP request per direction, small sets waste a request).
	for (const [key, entries] of groups) {
		if (key === 'other') {
			continue;
		}

		const outgoing = entries.filter((e) => e.direction === 'outgoing');
		const incoming = entries.filter((e) => e.direction === 'incoming');

		const outLarge = outgoing.length >= MIN_PRODUCT_GROUP_SIZE;
		const inLarge = incoming.length >= MIN_PRODUCT_GROUP_SIZE;

		if (!outLarge && !inLarge) {
			// Neither direction is large enough — merge entire group
			let otherGroup = groups.get('other');
			if (!otherGroup) {
				otherGroup = [];
				groups.set('other', otherGroup);
			}
			otherGroup.push(...entries);
			groups.delete(key);
		} else if (outLarge && !inLarge && incoming.length > 0) {
			// Outgoing is large, incoming is small — keep outgoing, move incoming to other
			let otherGroup = groups.get('other');
			if (!otherGroup) {
				otherGroup = [];
				groups.set('other', otherGroup);
			}
			otherGroup.push(...incoming);
			groups.set(key, outgoing);
		} else if (inLarge && !outLarge && outgoing.length > 0) {
			// Incoming is large, outgoing is small — keep incoming, move outgoing to other
			let otherGroup = groups.get('other');
			if (!otherGroup) {
				otherGroup = [];
				groups.set('other', otherGroup);
			}
			otherGroup.push(...outgoing);
			groups.set(key, incoming);
		}
		// else: both directions are large — keep everything in this group
	}

	// If still too many groups, keep folding the smallest into "other"
	while (groups.size > MAX_PRODUCT_BATCHES) {
		let smallestKey: string | null = null;
		let smallestSize = Infinity;
		for (const [key, entries] of groups) {
			if (key !== 'other' && entries.length < smallestSize) {
				smallestSize = entries.length;
				smallestKey = key;
			}
		}
		if (!smallestKey) {
			break;
		}
		let otherGroup = groups.get('other');
		if (!otherGroup) {
			otherGroup = [];
			groups.set('other', otherGroup);
		}
		otherGroup.push(...groups.get(smallestKey)!);
		groups.delete(smallestKey);
	}

	// Final pass: redistribute "other" entries into the largest existing
	// group of the same direction. Since DiscoverProductBatchAtom issues a
	// separate HTTP request per direction within each group, tiny
	// per-direction slices in "other" waste requests. By folding them into
	// an existing group that already has the same direction, the entries
	// piggyback on an HTTP request that was going to happen anyway.
	const otherGroup = groups.get('other');
	if (otherGroup && otherGroup.length > 0) {
		const otherOutgoing = otherGroup.filter((e) => e.direction === 'outgoing');
		const otherIncoming = otherGroup.filter((e) => e.direction === 'incoming');
		const kept: ProductBatchEntry[] = [];

		// For each direction, try to find the largest existing group with
		// entries in that direction and merge into it.
		for (const [subset, direction] of [
			[otherOutgoing, 'outgoing'],
			[otherIncoming, 'incoming'],
		] as const) {
			if (subset.length === 0) {
				continue;
			}

			// Find the group with the most entries in this direction
			let bestKey: string | null = null;
			let bestCount = 0;
			for (const [key, entries] of groups) {
				if (key === 'other') {
					continue;
				}
				const dirCount = entries.filter((e) => e.direction === direction).length;
				if (dirCount > bestCount) {
					bestCount = dirCount;
					bestKey = key;
				}
			}

			if (bestKey) {
				// Merge into the largest group of the same direction
				groups.get(bestKey)!.push(...subset);
			} else {
				// No group has this direction — keep in "other"
				kept.push(...subset);
			}
		}

		if (kept.length > 0) {
			groups.set('other', kept);
		} else {
			groups.delete('other');
		}
	}

	return groups;
}

// ============================================================================
// BFS shortest path
// ============================================================================

/**
 * Finds the shortest path from `startAri` to `targetAri` using BFS.
 *
 * The `parentMap` maps each child ARI to the set of parent ARIs that
 * produced it (via selected relationships). The BFS traverses in reverse
 * — from the target back towards the start — because the parent map
 * stores child→parents edges.
 *
 * Returns the path as an array of ARIs from start to target (inclusive),
 * or an empty array if no path exists.
 */
export function bfsShortestPath(
	startAri: string,
	targetAri: string,
	parentMap: Map<string, Set<string>>,
): string[] {
	if (startAri === targetAri) {
		return [startAri];
	}

	const visited = new Set<string>([targetAri]);
	const prev = new Map<string, string>();
	const queue: string[] = [targetAri];

	while (queue.length > 0) {
		const current = queue.shift()!;
		const parents = parentMap.get(current);
		if (!parents) {
			continue;
		}

		for (const parent of parents) {
			if (visited.has(parent)) {
				continue;
			}
			visited.add(parent);
			prev.set(parent, current);

			if (parent === startAri) {
				const path: string[] = [startAri];
				let node = startAri;
				while (node !== targetAri) {
					node = prev.get(node)!;
					path.push(node);
				}
				return path;
			}

			queue.push(parent);
		}
	}

	return [];
}

/**
 * Finds the shortest path from `startAri` to `targetAri` using BFS
 * over a bidirectional adjacency map.
 *
 * Unlike `bfsShortestPath` (which traverses child→parent edges only),
 * this function treats edges as undirected: if A is a neighbour of B,
 * then B is also a neighbour of A.
 *
 * The `adjacency` map stores `nodeAri → Set<neighbourAri>` and must
 * already be bidirectional (both directions recorded).
 *
 * Returns the path as an array of ARIs from start to target (inclusive),
 * or an empty array if no path exists.
 */
export function bfsBidirectionalPath(
	startAri: string,
	targetAri: string,
	adjacency: Map<string, Set<string>>,
): string[] {
	if (startAri === targetAri) {
		return [startAri];
	}

	const visited = new Set<string>([startAri]);
	const prev = new Map<string, string>();
	const queue: string[] = [startAri];

	while (queue.length > 0) {
		const current = queue.shift()!;
		const neighbours = adjacency.get(current);
		if (!neighbours) {
			continue;
		}

		for (const neighbour of neighbours) {
			if (visited.has(neighbour)) {
				continue;
			}
			visited.add(neighbour);
			prev.set(neighbour, current);

			if (neighbour === targetAri) {
				// Reconstruct path from start → target
				const path: string[] = [];
				let node: string | undefined = targetAri;
				while (node !== undefined) {
					path.push(node);
					node = prev.get(node);
				}
				path.reverse();
				return path;
			}

			queue.push(neighbour);
		}
	}

	return [];
}

// ============================================================================
// Relationship edge colors
// ============================================================================

/**
 * Color palette for relationship edges, using ADS chart palette hex values.
 */
export const RELATIONSHIP_COLORS = [
	'#1D7AFC', // blue
	'#8270DB', // purple
	'#22A06B', // green
	'#D97008', // orange
	'#E34935', // red
	'#1D9AAA', // teal
	'#B38600', // yellow
	'#CD519D', // magenta
	'#6A9A23', // lime
	'#758195', // gray
] as const;

/**
 * Gets a color for a relationship based on its index in the current hierarchy.
 */
export function getRelationshipColor(index: number): string {
	return RELATIONSHIP_COLORS[index % RELATIONSHIP_COLORS.length];
}

// ============================================================================
// GraphQL error detection helpers
// ============================================================================

/**
 * Determines whether a GraphQL response represents a complete query failure.
 *
 * A query is considered failed when:
 * 1. The `errors` array is present and non-empty, AND
 * 2. `data.graphStore` is null/undefined (no usable data was returned)
 *
 * This distinguishes total failures from partial successes where some data
 * is returned alongside errors.
 */
export function isGraphQLQueryFailure(response: any): boolean {
	if (!response) {
		return false;
	}
	const hasErrors = Array.isArray(response.errors) && response.errors.length > 0;
	const hasNoData = response.data?.graphStore == null;
	return hasErrors && hasNoData;
}

/**
 * Extracts the gateway trace ID from a GraphQL response.
 *
 * The trace ID is located at `extensions.gateway.trace_id` in the response.
 * Returns null if not present.
 */
export function extractTraceId(response: any): string | null {
	return response?.extensions?.gateway?.trace_id ?? null;
}

// ============================================================================
// Cache helpers
// ============================================================================

/**
 * Cache entry for a query result (discovery or node loading).
 */
export interface CachedQueryResult {
	/** The raw network result */
	result: any;
	/** Timestamp when cached */
	cachedAt: number;
}

/**
 * Generates a cache key for a node loading query.
 */
export function makeNodesCacheKey(
	nodeAri: string,
	direction: ExplorationDirection,
	relType: string,
): string {
	return `${nodeAri}:${direction}:${relType}`;
}

/**
 * Generates a cache key for a discovery query.
 */
export function makeDiscoveryCacheKey(nodeAri: string, direction: ExplorationDirection): string {
	return `${nodeAri}:discover:${direction}`;
}

/**
 * Checks if a cache entry exists and is valid.
 *
 * Cache entries persist for the entire user session on the explorer.
 * This avoids redundant network calls when the user navigates back
 * to previously explored nodes. The cache is cleared naturally when
 * the user leaves the explorer page (component unmount).
 */
export function isCacheValid(entry: CachedQueryResult | undefined): entry is CachedQueryResult {
	return entry !== undefined;
}

/**
 * Generates a selected-relationship key from its components.
 */
export function makeSelectedRelKey(
	sourceNodeAri: string,
	direction: ExplorationDirection,
	relType: string,
): string {
	return `${sourceNodeAri}:${direction}:${relType}`;
}

/**
 * Whether a RelationshipInfo entry was produced by splitting a single
 * relationship type into per-node-type rows.  Display layers should use
 * this instead of hardcoding relationship name checks.
 */
export function isSplitRelationship(rel: RelationshipInfo): boolean {
	return rel.sourceRelType != null && rel.targetNodeType != null;
}

/**
 * Builds a synthetic AGG-shaped result object from a list of
 * DiscoveredNodes.  Used to populate QueryCacheAtom with
 * pre-hydrated data for split relationship entries so the toggle
 * path can reuse the standard extractNodesFromResult() pipeline.
 */
export function buildSyntheticResult(nodes: DiscoveredNode[]): any {
	return {
		data: {
			graphStore: {
				cypherQueryV2: {
					edges: nodes.map((node: DiscoveredNode) => ({
						node: {
							columns: [
								{
									key: 'm',
									value: {
										id: node.id,
										data: {
											__typename: node.typename,
											...node.properties,
										},
									},
								},
							],
						},
					})),
				},
			},
		},
	};
}
